import { useState } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { Link, useNavigate } from "react-router-dom";
import { AxiosError } from "axios";

import { LayoutAuth } from "../../../../components/LayoutAuth";
import { Switch } from "../../../../components/Switch";
import { Eye, EyeSlash } from "../../../../assets/svg";

import {
  Title,
  Form,
  Musthave,
  Rule,
  ContentStep1,
  LinkSignIn,
} from "./styles";
import { IOptionTypeUsername, IRecoverPassword } from "../../interfaces";
import { Input } from "../../../../components/Input";
import { Phone } from "../../../../components/PhoneInput";
import { validatePassword } from "../../../../utils/regex";
import { Button } from "../../../../components/Button";
import { useAuth } from "../../../../hooks/useAuth";
import { useNotification } from "../../../../hooks/useNotification";
import { TypeUsername } from "../../enum";
import { useTranslate } from "../../../../hooks/useTranslate";
import { Stack } from "@mui/material";

const RecoverPassword = () => {
  const { verifyUsername, confirmNewPassword } = useAuth();
  const { errorNotification, successNotification } = useNotification();
  const { translate } = useTranslate();
  const navigate = useNavigate();

  const [step, setStep] = useState(1);
  const [username, setUsername] = useState("");
  const [errorUsername, setErrorUsername] = useState("");
  const [showVerifyPassword, setShowVerifyPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [signType, setSignType] = useState<TypeUsername>(TypeUsername.email);
  const [rulesPassword, setRulesPassword] = useState({
    length_char: false,
    upper_case: false,
    lower_case: false,
    number: false,
    special_char: false,
  });

  const schema = yup.object({
    password: yup.string().required(translate("global.password_required")),
    code: yup.string().required(translate("global.code_required")),
  });

  const {
    register,
    handleSubmit,
    setError,
    clearErrors,
    setValue,
    formState: { errors },
  } = useForm<IRecoverPassword>({
    resolver: yupResolver(schema),
  });

  const submitForm = async ({ code, password }: IRecoverPassword) => {
    try {
      await confirmNewPassword({
        value: username,
        code,
        password,
      });

      successNotification(translate("auth.success_changed_password"));
      setIsLoading(false);
      navigate("/");
    } catch (error) {
      errorNotification(translate("auth.error_code"));
      setIsLoading(false);
    }
  };

  const options: IOptionTypeUsername[] = [
    { value: TypeUsername.email, label: translate("global.email") },
    { value: TypeUsername.phone, label: translate("global.phone") },
  ];

  const selectSignType = (value: TypeUsername) => {
    setSignType(value);
  };

  const checkUsername = async () => {
    if (username.length <= 0) {
      const keyTranslate =
        signType === TypeUsername.email
          ? "global.email_required"
          : "global.phone_required";
      setErrorUsername(translate(keyTranslate));
      return;
    }

    setErrorUsername("");
    setIsLoading(true);

    try {
      await verifyUsername(username);
      setStep(2);
      setIsLoading(false);
      successNotification(translate("global.success_send_code"));
    } catch (error) {
      const err = error as AxiosError;

      setIsLoading(false);
      if (err.response?.status === 404) {
        errorNotification(translate("auth.user_not_found"));
        return;
      }
      errorNotification(translate("auth.generic_error"));
    }
  };

  const checkPassword = (value: string) => {
    if (!value) return;

    const { length_char, upper_case, lower_case, number, special_char } =
      validatePassword(value);

    if (
      [length_char, upper_case, lower_case, number, special_char].every(
        (element) => element
      )
    ) {
      clearErrors("password");
    } else {
      setError("password", {
        message: translate("global.password_invalid"),
      });
    }

    setValue("password", value);
    setRulesPassword({
      length_char,
      upper_case,
      lower_case,
      number,
      special_char,
    });
  };

  return (
    <LayoutAuth>
      <Title>{translate("auth.title_recover_password")}</Title>

      {step === 1 && (
        <Stack spacing={5}>
          <Switch
            currentActive={signType}
            options={options}
            onChange={(value) => {
              selectSignType(value as any);
            }}
          />

          {signType === TypeUsername.email ? (
            <Input
              onChange={(event) => {
                setUsername(event.target.value);
              }}
              error={errorUsername}
              placeholder={translate("global.placeholder_email")}
              type="email"
            />
          ) : (
            <Phone
              placeholder={translate("global.placeholder_phone")}
              value={username}
              onChange={setUsername}
              defaultCountry="BR"
              error={errorUsername}
            />
          )}

          <Button
            label={translate("auth.btn_recover")}
            type="button"
            disabled={!username.length}
            onClick={checkUsername}
            isLoading={isLoading}
          />

          <LinkSignIn>
            <Link to="/">{translate("auth.go_login")}</Link>
          </LinkSignIn>
        </Stack>
      )}

      {step === 2 && (
        <Form onSubmit={handleSubmit(submitForm)}>
          <Input
            {...register("code")}
            error={errors.code?.message}
            placeholder={translate("global.placeholder_code")}
            type="number"
          />
          <Input
            {...register("password")}
            error={errors.password?.message}
            onChange={(e) => {
              checkPassword(e.target.value);
            }}
            placeholder={translate("global.placeholder_password")}
            onFocus={() => {
              setShowVerifyPassword(true);
            }}
            onBlur={() => {
              setShowVerifyPassword(false);
            }}
            type={showPassword ? "text" : "password"}
            cbIcon={() => {
              setShowPassword(!showPassword);
            }}
            icon={showPassword ? <EyeSlash /> : <Eye />}
          />
          <Musthave isVisible={showVerifyPassword}>
            <Rule isCheked={rulesPassword.upper_case}>
              {translate("global.password_rule_1")}
            </Rule>
            <Rule isCheked={rulesPassword.lower_case}>
              {translate("global.password_rule_2")}
            </Rule>
            <Rule isCheked={rulesPassword.number}>
              {translate("global.password_rule_3")}
            </Rule>
            <Rule isCheked={rulesPassword.special_char}>
              {translate("global.password_rule_4")}
            </Rule>
            <Rule isCheked={rulesPassword.length_char}>
              {translate("global.password_rule_5")}
            </Rule>
          </Musthave>
          <Button
            label={translate("auth.btn_new_password")}
            type="submit"
            isLoading={isLoading}
          />
          <LinkSignIn>
            <Link to="/">{translate("auth.go_login")}</Link>
          </LinkSignIn>
        </Form>
      )}
    </LayoutAuth>
  );
};

export { RecoverPassword };
